import { motion } from "framer-motion";
import { forwardRef, useEffect, useRef, useState } from "react";
import { useProductData } from "@/zustand/productStore.js";
import { useCartData } from "@/zustand/cartStore";
import { useReducedProductsPrice } from "@/func/productCardHelpers.js";
import LazyLoad from "react-lazyload";
import ProductSelectorButton from "@children/components/ProductSelectorButton";
import ProductPriceBlock from "@/components/ProductPackages/PriceBlock";
import DoubleTagIcon from "@/public/fallbacks/DoubleTagIcon.svg";
import { formatDisplayName } from "@/utils/helpers.jsx";
import glideImage from "@/func/glideImage.js";
import { useUpsellData } from "@children/zustand/upsellStore";
import s from "./ProductPackages.module.scss";
import { ChevronDownIcon } from "@/utils/miscIcons.jsx";

const UpsellItem = forwardRef((props, ref) => {
  const productData = useProductData();
  const { upsellProduct } = useUpsellData();
  const [showMore, setShowMore] = useState(false);
  const base = window.post_template;
  const { selectedProduct } = useCartData();
  const { setUpsellProduct } = useUpsellData();
  const { product } = props;
  const [checked, setChecked] = useState(false);
  const variantsRef = useRef();
  const productsContent = window.post_template?.checkout_v4_products;
  const outlineBorder = productsContent.checkout_v4_products_imageWrap;
  const showBadges = productsContent.checkout_v4_products_badgeShow;
  const showBulletPoints = base?.checkout_v4_summary.checkout_v4_summary_bullet_points ?? false;

  const formattedProduct = {
    price: product?.product_list_unit_price,
    amount: selectedProduct?.amount,
    old_price: product?.product_list_unit_old_price,
    promo_discounted_price: product?.product_list_unit_price * selectedProduct?.amount,
  };
  const productInfo = useReducedProductsPrice(
    formattedProduct,
    productData?.currency_code,
    productData?.formatted_string
  );
  const { priceObject } = productInfo;
  const image = glideImage(product?.product_list_product_image, { h: 85 });
  const [maxHeight, setMaxHeight] = useState("0px");

  useEffect(() => {
    if (showMore && variantsRef.current) {
      setMaxHeight(`${variantsRef.current.scrollHeight}px`);
    } else {
      setMaxHeight("0px");
    }
  }, [showMore]);

  const handleChange = (product) => {
    if (upsellProduct?.product_list_unit_price === product?.product_list_unit_price) {
      setUpsellProduct(null);
      setChecked(false);
    } else {
      setUpsellProduct(product);
      setChecked(true);
    }
  };

  return (
    <div className="productPackages__container--unchecked">
      <div
        data-testid="product-container"
        onClick={() => handleChange(product)}
        id={`productCard-${selectedProduct?.amount}`}
        className={`productPackages__container`}
      >
        <div className="productPacItem__firstBlock">
          <div style={{ display: "flex", alignItems: "center" }}>
            {showBulletPoints !== undefined && showBulletPoints !== null && !showBulletPoints && (
              <ProductSelectorButton isChecked={checked} />
            )}
            <LazyLoad offset={100}>
              <motion.div
                key={image}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{
                  delay: 1,
                  default: { duration: 0.5 },
                }}
              >
                <img
                  className={`productPacItem__image ${s.sdCard} ${outlineBorder ? "productPacItem__image--border" : ""} mon-img-170-h`}
                  src={image}
                />
              </motion.div>
            </LazyLoad>
          </div>
          <div className={`productPacItem__label__container`}>
            <span
              data-testid="product-amount"
              className="productPacItem__label__amount"
              style={{ textTransform: "lowercase" }}
            >
              {selectedProduct?.amount}x
            </span>
            <span className="productPacItem__label">{formatDisplayName(product?.generic_name)}</span>
            <div className="productPacItem__discount__box desktopHide">
              <img src={DoubleTagIcon} />
              {product?.product_list_discount_percent} %
            </div>
          </div>
        </div>
        <div className="productPacItem__divider mobileHide"></div>
        <div className="productPacItem__discount__container mobileHide">
          <div className="productPacItem__discount__box" style={!showBadges ? { opacity: "0" } : {}}>
            <img src={DoubleTagIcon} />
            {product?.product_list_discount_percent}%
          </div>
        </div>

        <div className="productPacItem__divider"></div>

        <ProductPriceBlock
          priceObject={priceObject}
          oldPrice={productData?.formatted_string?.replace(/(0\.00|0,00|0)/, formattedProduct.old_price)}
          product={formattedProduct}
        />
      </div>
      <div className="variant-seperator"></div>
      {showMore && (
        <div
          className={`variants-container ${showMore ? s.show : s.hide}`}
          style={{ maxHeight, overflow: "hidden", transition: "max-height 0.3s ease" }}
        >
          <div ref={variantsRef} className={s.description}>
            <span style={{ fontWeight: "700" }}>ONE-TIME OFFER:</span>
            <br />
            <span style={{ fontWeight: "700" }}>Save $240/year on Cloud Storage – Get an SD Card now</span> and skip
            Cloud Storage payment! Record and review footage right from your iPhone or Android, no subscription needed.
          </div>
        </div>
      )}

      <div
        className={s.showMoreText}
        onClick={(e) => {
          e.preventDefault();
          setShowMore(!showMore);
        }}
      >
        {showMore ? "Show Less" : "Show More"}
        <span className={`${s.toggleIcon} ${showMore ? s.rotated : ""}`}>
          <ChevronDownIcon />
        </span>
      </div>
    </div>
  );
});

export default UpsellItem;
